import { DeleteOutlined } from "@ant-design/icons";
import { Button, Flex, List, Modal, Select, Tag, Typography, message } from "antd";
import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useFuseSearch } from "../../hooks/useFuseSearch";
import { createAutoShares, getAutoShares, removeAutoShare } from "../../service/client.service";
import type { TAutoShareEligibleMember, TClientAutoShare } from "../../types/index.type";

interface AutoShareManagerProps {
  /** Absent = manage the current user's own rules; present = a group admin
   *  managing this member's rules on their behalf. */
  owner?: { id: number; name: string };
}

/**
 * Standing "auto-share my patients" rules: every current patient (via
 * backfill) and every future patient (EHR overnight import, manual creation)
 * is shared with the selected group members. Removing someone stops future
 * sharing only — already-shared patients keep access.
 *
 * Recipient candidates come from the auto-share API's `eligible_members` —
 * scoped server-side to the same group boundary used for validation and
 * application. (The generic members endpoint only knows the REQUESTER's
 * first group, which is wrong for multi-group admins managing another
 * group's owner.)
 */
const AutoShareManager: React.FC<AutoShareManagerProps> = ({ owner }) => {
  const [loading, setLoading] = useState(false);
  const [eligibleMembers, setEligibleMembers] = useState<TAutoShareEligibleMember[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  // Names of everyone selected, keyed by id — resolved from the retained map
  // (never a search-filtered list) so every submitted recipient is named in
  // the confirmation dialog.
  const [selectedNames, setSelectedNames] = useState<Record<number, string>>({});
  const [rules, setRules] = useState<TClientAutoShare[]>([]);
  const [clientCount, setClientCount] = useState(0);
  const [loadingRules, setLoadingRules] = useState(false);
  const [removingUserId, setRemovingUserId] = useState<number | null>(null);
  const [searchValue, setSearchValue] = useState("");

  const ownerId = owner?.id;
  const whose = owner ? `${owner.name}'s` : "your";

  useEffect(() => {
    fetchRules();
  }, [ownerId]);

  const fetchRules = async () => {
    setLoadingRules(true);
    try {
      const response = await getAutoShares(ownerId);
      setRules(response.data.rules);
      setClientCount(response.data.client_count);
      setEligibleMembers(response.data.eligible_members ?? []);
    } catch (error) {
      console.error("Error fetching auto-share rules:", error);
      message.error("Auto-sharing is only available for group users");
    } finally {
      setLoadingRules(false);
    }
  };

  const submitAutoShares = async () => {
    setLoading(true);
    try {
      const response = await createAutoShares(selectedUserIds, ownerId);
      if ((response.data.created ?? 0) > 0) {
        message.success(`Auto-sharing enabled for ${response.data.created} member(s)`);
      }
      if (response.data.already_active && response.data.already_active.length > 0) {
        message.info(`${response.data.already_active.length} member(s) already had auto-sharing enabled`);
      }
      if (response.data.invalid_users && response.data.invalid_users.length > 0) {
        message.warning(`${response.data.invalid_users.length} member(s) are not in the group`);
      }
      setRules(response.data.rules);
      setClientCount(response.data.client_count);
      setEligibleMembers(response.data.eligible_members ?? []);
      setSelectedUserIds([]);
      setSelectedNames({});
    } catch (error) {
      console.error("Error enabling auto-sharing:", error);
      message.error("Failed to enable auto-sharing");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectionChange = (ids: number[]) => {
    setSelectedUserIds(ids);
    setSelectedNames((prev) => {
      const next: Record<number, string> = {};
      for (const id of ids) {
        const member = eligibleMembers.find((m) => m.id === id);
        next[id] = member?.name ?? prev[id] ?? `Member #${id}`;
      }
      return next;
    });
  };

  const handleEnable = () => {
    if (selectedUserIds.length === 0) {
      message.warning("Please select at least one member");
      return;
    }
    // Every submitted recipient must be named — resolved from the retained
    // selection map, never from a search-filtered list.
    const names = selectedUserIds.map((id) => selectedNames[id] ?? `Member #${id}`).join(", ");

    // Step 1: what will happen.
    Modal.confirm({
      title: "Turn on auto-sharing?",
      width: 520,
      okText: "Continue",
      cancelText: "Cancel",
      content: (
        <Flex vertical gap={8}>
          <Typography.Text>
            You're about to auto-share <b>{whose} patients</b> with: <b>{names}</b>.
          </Typography.Text>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            <li>
              All <b>{clientCount} current patient(s)</b> will be shared with them <b>immediately</b>.
            </li>
            <li>
              <b>Every future patient</b> — including patients imported from the EHR overnight and patients added
              manually — will be shared with them <b>automatically</b>.
            </li>
          </ul>
          <Typography.Text>
            They will be able to open these patients and create notes on them, exactly as if each patient had been
            shared individually.
          </Typography.Text>
        </Flex>
      ),
      onOk: () => {
        // Step 2: the irreversibility warning.
        Modal.confirm({
          title: "Please confirm — this is effectively irreversible",
          width: 520,
          okText: `Yes, share all ${clientCount} patient(s)`,
          okButtonProps: { danger: true },
          cancelText: "Go back",
          content: (
            <Flex vertical gap={8}>
              <Typography.Text>
                Removing <b>{names}</b> from auto-sharing later will only stop <b>new</b> patients from being shared. It
                will <b>NOT</b> take back access to patients that have already been shared — undoing that would require
                revoking access <b>one patient at a time</b>.
              </Typography.Text>
              <Typography.Text>
                Share all <b>{clientCount} current patient(s)</b> and <b>all future patients</b> with <b>{names}</b>?
              </Typography.Text>
            </Flex>
          ),
          onOk: submitAutoShares,
        });
      },
    });
  };

  const handleRemove = (rule: TClientAutoShare) => {
    Modal.confirm({
      title: `Stop auto-sharing with ${rule.shared_with_name}?`,
      okText: "Stop auto-sharing",
      cancelText: "Cancel",
      content: (
        <Typography.Text>
          New patients will no longer be shared with them. Patients already shared with them will <b>keep access</b>{" "}
          until you revoke it on each patient individually.
        </Typography.Text>
      ),
      onOk: async () => {
        setRemovingUserId(rule.shared_with);
        try {
          await removeAutoShare(rule.shared_with, ownerId);
          message.success("Auto-sharing stopped. Already-shared patients keep access.");
          await fetchRules();
        } catch (error) {
          console.error("Error removing auto-share rule:", error);
          message.error("Failed to stop auto-sharing");
        } finally {
          setRemovingUserId(null);
        }
      },
    });
  };

  // Exclude members who already have a rule (the API already excludes the owner).
  const ruleUserIds = rules.map((rule) => rule.shared_with);
  const availableMembersBase = useMemo(
    () => eligibleMembers.filter((member) => member.id && !ruleUserIds.includes(member.id)),
    [eligibleMembers, ruleUserIds],
  );

  const { search: fuseSearch } = useFuseSearch({
    data: availableMembersBase,
    keys: ["name", "email"],
    threshold: 0.3,
  });

  const availableMembers = useMemo(() => {
    if (!searchValue.trim()) {
      return availableMembersBase;
    }
    return fuseSearch(searchValue);
  }, [availableMembersBase, searchValue, fuseSearch]);

  const handleSearch = useCallback((value: string) => {
    setSearchValue(value);
  }, []);

  return (
    <Flex vertical gap={20}>
      <Flex vertical gap={4}>
        <Typography.Text type="secondary" style={{ fontSize: "13px" }}>
          Automatically share all of {whose} patients — current and future (including nightly EHR imports) — with
          selected group members.
        </Typography.Text>
      </Flex>

      <Flex vertical gap={10}>
        <Typography.Text strong>Auto-share with:</Typography.Text>
        <Select
          mode="multiple"
          placeholder="Search by name or email to select members"
          value={selectedUserIds}
          onChange={handleSelectionChange}
          style={{ width: "100%" }}
          showSearch
          filterOption={false}
          onSearch={handleSearch}
          searchValue={searchValue}
          loading={loadingRules}
          notFoundContent={loadingRules ? "Loading..." : "No members found"}
          options={availableMembers.map((member) => ({
            label: `${member.name} (${member.email})`,
            value: member.id,
          }))}
        />
        <Button type="primary" onClick={handleEnable} loading={loading} disabled={selectedUserIds.length < 1}>
          Enable auto-sharing
        </Button>
      </Flex>

      <Flex vertical gap={10}>
        <Typography.Text strong>Auto-shared with ({rules.length}):</Typography.Text>
        {loadingRules ? (
          <Typography.Text type="secondary">Loading...</Typography.Text>
        ) : rules.length > 0 ? (
          <List
            dataSource={rules}
            renderItem={(rule) => (
              <List.Item
                actions={[
                  <Button
                    key="remove"
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleRemove(rule)}
                    loading={removingUserId === rule.shared_with}
                  >
                    Remove
                  </Button>,
                ]}
              >
                <List.Item.Meta
                  title={
                    <>
                      {rule.shared_with_name}{" "}
                      {!rule.in_group && (
                        <Tag color="default" style={{ marginLeft: 4 }}>
                          no longer in the group
                        </Tag>
                      )}
                    </>
                  }
                  description={rule.shared_with_email}
                />
              </List.Item>
            )}
          />
        ) : (
          <Typography.Text type="secondary">
            {owner ? `${owner.name} is` : "You are"} not auto-sharing patients with anyone yet
          </Typography.Text>
        )}
      </Flex>
    </Flex>
  );
};

export default AutoShareManager;
